import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FaChevronDown, FaDownload, FaFileUpload, FaSyncAlt, FaTrashAlt } from 'react-icons/fa'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api, { apiDownload } from '../../../helpers/apiRequester'
import { CollapsibleSection } from './collapsibleSection'
import styles from './communicationsPanel.module.scss'

interface CommunicationGroup {
    id: number
    omnibus_ref: string | null
    defendant: string
}

interface CommunicationSummary {
    id: number
    subject: string | null
    thread_topic: string | null
    from_email: string | null
    to_emails: string[]
    cc_emails: string[]
    first_message_at: string | null
    last_message_at: string | null
    message_count: number
    defendants: string[]
    groups: CommunicationGroup[]
}

interface CommunicationMessage {
    id: number
    position: number
    from_email: string | null
    to_emails: string[]
    cc_emails: string[]
    subject: string | null
    sent_at: string | null
    body_text: string | null
}

interface CommunicationDetail extends CommunicationSummary {
    messages: CommunicationMessage[]
}

interface CommunicationsPanelProps {
    omnibusId: number
    defendant: string
    omnibusRef: string
    /** Bumped by the parent after an import so the list reloads. */
    refreshKey?: number
    onUpload?: () => void
    uploading?: boolean
    onChanged?: () => void
    /** Show destructive controls (Remove email). Admin-only on the detail page. */
    canManage?: boolean
}

const formatDateTime = (value: string | null) => {
    if (!value) return 'Date unavailable'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)
}

const addresses = (values?: string[]) => (values?.length ? values.join(', ') : '—')

/** Split an RFC "Display Name <inbox@example.com>" value into its two parts. */
const splitAddress = (value: string | null) => {
    const raw = (value || '').trim()
    if (!raw) return { name: 'Unknown sender', email: null as string | null }
    const match = raw.match(/^(.*?)<([^>]+)>$/)
    if (!match) return { name: raw, email: null as string | null }
    const name = match[1].replace(/^["'\s]+|["'\s]+$/g, '')
    return { name: name || match[2].trim(), email: match[2].trim() }
}

const snippet = (value: string | null) => {
    const text = (value || '').replace(/\s+/g, ' ').trim()
    if (!text) return 'No plain-text body'
    return text.length > 140 ? `${text.slice(0, 140)}…` : text
}

const messageKey = (communicationId: number, messageId: number) => `${communicationId}:${messageId}`

const newestFirst = (messages: CommunicationMessage[]) => [...messages].sort((a, b) => b.position - a.position)

export const CommunicationsPanel = ({
    omnibusId,
    defendant,
    omnibusRef,
    refreshKey = 0,
    onUpload,
    uploading = false,
    onChanged,
    canManage = true,
}: CommunicationsPanelProps) => {
    const [communications, setCommunications] = useState<CommunicationSummary[]>([])
    const [details, setDetails] = useState<Record<number, CommunicationDetail>>({})
    const [expandedId, setExpandedId] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)
    const [detailLoadingId, setDetailLoadingId] = useState<number | null>(null)
    const [downloadingId, setDownloadingId] = useState<number | null>(null)
    const [removingId, setRemovingId] = useState<number | null>(null)
    const [openMessages, setOpenMessages] = useState<Record<string, boolean>>({})
    const [error, setError] = useState<string | null>(null)
    const [detailErrors, setDetailErrors] = useState<Record<number, string>>({})

    const fetchCommunications = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.post('omnibus-communications-list', { omnibus_id: omnibusId })
            if (response.data?.success === false) {
                throw new Error(response.data.message || 'Could not load communications.')
            }
            setCommunications(response.data?.communications || [])
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load communications.')
        } finally {
            setLoading(false)
        }
    }, [omnibusId])

    useEffect(() => {
        setExpandedId(null)
        setDetails({})
        setOpenMessages({})
        fetchCommunications()
    }, [fetchCommunications, refreshKey])

    const sortedCommunications = useMemo(
        () =>
            [...communications].sort(
                (a, b) =>
                    new Date(b.last_message_at || b.first_message_at || 0).getTime() -
                    new Date(a.last_message_at || a.first_message_at || 0).getTime(),
            ),
        [communications],
    )

    const loadDetail = useCallback(
        async (communicationId: number) => {
            if (details[communicationId]) return
            setDetailLoadingId(communicationId)
            setDetailErrors((current) => {
                const next = { ...current }
                delete next[communicationId]
                return next
            })
            try {
                const response = await api.post('omnibus-communication-detail', {
                    omnibus_id: omnibusId,
                    communication_id: communicationId,
                })
                if (response.data?.success === false || !response.data?.communication) {
                    throw new Error(response.data?.message || 'Could not load this thread.')
                }
                const communication = response.data.communication as CommunicationDetail
                setDetails((current) => ({ ...current, [communicationId]: communication }))
                // Open the latest email only; earlier ones stay one click away.
                const latest = newestFirst(communication.messages || [])[0]
                if (latest) {
                    setOpenMessages((current) => ({
                        ...current,
                        [messageKey(communicationId, latest.id)]: true,
                    }))
                }
            } catch (err: any) {
                setDetailErrors((current) => ({
                    ...current,
                    [communicationId]: err?.response?.data?.message || err?.message || 'Failed to load this thread.',
                }))
            } finally {
                setDetailLoadingId(null)
            }
        },
        [details, omnibusId],
    )

    const toggleThread = useCallback(
        (communicationId: number) => {
            if (expandedId === communicationId) {
                setExpandedId(null)
                return
            }
            setExpandedId(communicationId)
            loadDetail(communicationId)
        },
        [expandedId, loadDetail],
    )

    const toggleMessage = useCallback((communicationId: number, messageId: number) => {
        const key = messageKey(communicationId, messageId)
        setOpenMessages((current) => ({ ...current, [key]: !current[key] }))
    }, [])

    const setAllMessages = useCallback((communicationId: number, messages: CommunicationMessage[], open: boolean) => {
        setOpenMessages((current) => {
            const next = { ...current }
            for (const message of messages) next[messageKey(communicationId, message.id)] = open
            return next
        })
    }, [])

    const downloadRaw = useCallback(
        async (communication: CommunicationSummary) => {
            setDownloadingId(communication.id)
            try {
                const response = await apiDownload('omnibus-communication-raw', {
                    omnibus_id: omnibusId,
                    communication_id: communication.id,
                })
                const disposition = response.headers['content-disposition'] || ''
                const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
                const filename = filenameMatch?.[1]
                    ? decodeURIComponent(filenameMatch[1])
                    : `omnibus-communication-${communication.id}.eml`
                const blobUrl = URL.createObjectURL(response.data)
                const anchor = document.createElement('a')
                anchor.href = blobUrl
                anchor.download = filename
                document.body.appendChild(anchor)
                anchor.click()
                document.body.removeChild(anchor)
                URL.revokeObjectURL(blobUrl)
            } catch (err: any) {
                setDetailErrors((current) => ({
                    ...current,
                    [communication.id]: err?.response?.data?.message || err?.message || 'Raw EML download failed.',
                }))
            } finally {
                setDownloadingId(null)
            }
        },
        [omnibusId],
    )

    const removeCommunication = useCallback(
        (communication: CommunicationSummary) => {
            const label = communication.thread_topic || communication.subject || 'this email'
            Confirm.show(
                'Remove email',
                `Remove "${label}" from ${defendant}? The original .eml stays in secure storage and can be uploaded again.`,
                'Remove',
                'Cancel',
                async () => {
                    setRemovingId(communication.id)
                    try {
                        const response = await api.post('omnibus-communication-delete', {
                            omnibus_id: omnibusId,
                            communication_id: communication.id,
                        })
                        if (response.data?.success === false) {
                            throw new Error(response.data?.message || 'Could not remove this email.')
                        }
                        setExpandedId((current) => (current === communication.id ? null : current))
                        setDetails((current) => {
                            const next = { ...current }
                            delete next[communication.id]
                            return next
                        })
                        setCommunications((current) => current.filter((item) => item.id !== communication.id))
                        onChanged?.()
                        Report.success('Email removed', response.data?.message || 'The email is no longer attached.', 'Close')
                    } catch (err: any) {
                        Report.failure(
                            'Removal failed',
                            err?.response?.data?.message || err?.message || 'Unknown error',
                            'Close',
                        )
                    } finally {
                        setRemovingId(null)
                    }
                },
                () => undefined,
            )
        },
        [defendant, omnibusId, onChanged],
    )

    return (
        <CollapsibleSection
            title="Defendant communications"
            count={
                loading && communications.length === 0
                    ? 'Loading…'
                    : `${communications.length} thread${communications.length === 1 ? '' : 's'}`
            }
            hint={
                <>
                    Email chains with {defendant} ({omnibusRef}). Any .eml exported from Outlook is attached to this
                    lender, whatever the file name or subject line says, and shown here as plain text. Removing a thread
                    detaches it here; the original file stays in secure storage.
                </>
            }
            actions={
                <>
                    {onUpload && (
                        <button type="button" className={styles.uploadButton} onClick={onUpload} disabled={uploading}>
                            <FaFileUpload /> {uploading ? 'Uploading…' : 'Upload .eml files'}
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.headerButton}
                        onClick={fetchCommunications}
                        disabled={loading}
                    >
                        <FaSyncAlt /> {loading ? 'Loading…' : 'Refresh'}
                    </button>
                </>
            }
        >
            {error && (
                <div className={styles.error} role="alert">
                    <span>{error}</span>
                    <button type="button" onClick={fetchCommunications}>
                        Try again
                    </button>
                </div>
            )}

            {loading && communications.length === 0 && <div className={styles.state}>Loading communications…</div>}

            {!loading && !error && sortedCommunications.length === 0 && (
                <div className={styles.state}>
                    No emails attached yet. Use Upload .eml files to attach the Outlook chain for this lender.
                </div>
            )}

            <div className={styles.threadList}>
                {sortedCommunications.map((communication) => {
                    const isExpanded = expandedId === communication.id
                    const detail = details[communication.id]
                    const threadMessages = detail ? newestFirst(detail.messages || []) : []
                    const sharedWith = (communication.groups || [])
                        .filter((group) => group.id !== omnibusId)
                        .map((group) => group.defendant)
                    return (
                        <article key={communication.id} className={styles.threadCard}>
                            <button
                                type="button"
                                className={styles.threadSummary}
                                onClick={() => toggleThread(communication.id)}
                                aria-expanded={isExpanded}
                            >
                                <span className={styles.threadCopy}>
                                    <span className={styles.threadSubject}>
                                        {communication.thread_topic || communication.subject || '(No subject)'}
                                    </span>
                                    <span className={styles.threadParticipants}>
                                        From {communication.from_email || 'unknown sender'} · To{' '}
                                        {addresses(communication.to_emails)}
                                    </span>
                                    <span className={styles.threadMeta}>
                                        {formatDateTime(communication.last_message_at)} ·{' '}
                                        {communication.message_count} message
                                        {communication.message_count === 1 ? '' : 's'}
                                        {sharedWith.length > 0 && ` · also sent for ${sharedWith.join(', ')}`}
                                    </span>
                                </span>
                                <FaChevronDown className={`${styles.chevron} ${isExpanded ? styles.open : ''}`} />
                            </button>

                            {isExpanded && (
                                <div className={styles.threadDetail}>
                                    {detailLoadingId === communication.id && (
                                        <div className={styles.stateSmall}>Loading messages…</div>
                                    )}
                                    {detailErrors[communication.id] && (
                                        <div className={styles.inlineError} role="alert">
                                            {detailErrors[communication.id]}
                                        </div>
                                    )}
                                    {detail && threadMessages.length > 0 && (
                                        <>
                                            <div className={styles.messagesToolbar}>
                                                <span className={styles.messagesCount}>
                                                    {threadMessages.length} email
                                                    {threadMessages.length === 1 ? '' : 's'} in this chain, newest first
                                                </span>
                                                <span className={styles.messagesControls}>
                                                    <button
                                                        type="button"
                                                        className={styles.linkButton}
                                                        onClick={() =>
                                                            setAllMessages(communication.id, threadMessages, true)
                                                        }
                                                    >
                                                        Expand all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.linkButton}
                                                        onClick={() =>
                                                            setAllMessages(communication.id, threadMessages, false)
                                                        }
                                                    >
                                                        Collapse all
                                                    </button>
                                                </span>
                                            </div>
                                            <div className={styles.messages}>
                                                {threadMessages.map((message, index) => {
                                                    const messageOpen =
                                                        openMessages[messageKey(communication.id, message.id)] === true
                                                    const sender = splitAddress(message.from_email)
                                                    return (
                                                        <section
                                                            key={message.id}
                                                            className={`${styles.messageCard} ${
                                                                messageOpen ? styles.messageCardOpen : ''
                                                            }`}
                                                        >
                                                            <button
                                                                type="button"
                                                                className={styles.messageToggle}
                                                                onClick={() =>
                                                                    toggleMessage(communication.id, message.id)
                                                                }
                                                                aria-expanded={messageOpen}
                                                            >
                                                                <span className={styles.messageIndex}>
                                                                    {index + 1}
                                                                </span>
                                                                <span className={styles.messageHeadline}>
                                                                    <span className={styles.messageTop}>
                                                                        <span className={styles.messageSender}>
                                                                            {sender.name}
                                                                        </span>
                                                                        <time className={styles.messageTime}>
                                                                            {formatDateTime(message.sent_at)}
                                                                        </time>
                                                                    </span>
                                                                    {messageOpen ? (
                                                                        <span className={styles.messageSubject}>
                                                                            {message.subject || '(No subject)'}
                                                                        </span>
                                                                    ) : (
                                                                        <span className={styles.messageSnippet}>
                                                                            {snippet(message.body_text)}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <FaChevronDown
                                                                    className={`${styles.chevron} ${
                                                                        messageOpen ? styles.open : ''
                                                                    }`}
                                                                />
                                                            </button>

                                                            {messageOpen && (
                                                                <div className={styles.messageBody}>
                                                                    <dl className={styles.fieldGrid}>
                                                                        <div className={styles.field}>
                                                                            <dt className={styles.fieldLabel}>From</dt>
                                                                            <dd className={styles.fieldValue}>
                                                                                {sender.name}
                                                                                {sender.email && (
                                                                                    <span
                                                                                        className={styles.fieldAddress}
                                                                                    >
                                                                                        {sender.email}
                                                                                    </span>
                                                                                )}
                                                                            </dd>
                                                                        </div>
                                                                        <div className={styles.field}>
                                                                            <dt className={styles.fieldLabel}>To</dt>
                                                                            <dd
                                                                                className={`${styles.fieldValue} ${styles.fieldAddress}`}
                                                                            >
                                                                                {addresses(message.to_emails)}
                                                                            </dd>
                                                                        </div>
                                                                        {message.cc_emails?.length > 0 && (
                                                                            <div className={styles.field}>
                                                                                <dt className={styles.fieldLabel}>
                                                                                    Cc
                                                                                </dt>
                                                                                <dd
                                                                                    className={`${styles.fieldValue} ${styles.fieldAddress}`}
                                                                                >
                                                                                    {addresses(message.cc_emails)}
                                                                                </dd>
                                                                            </div>
                                                                        )}
                                                                        <div className={styles.field}>
                                                                            <dt className={styles.fieldLabel}>Sent</dt>
                                                                            <dd className={styles.fieldValue}>
                                                                                {formatDateTime(message.sent_at)}
                                                                            </dd>
                                                                        </div>
                                                                        <div className={styles.field}>
                                                                            <dt className={styles.fieldLabel}>
                                                                                Subject
                                                                            </dt>
                                                                            <dd className={styles.fieldValue}>
                                                                                {message.subject || '(No subject)'}
                                                                            </dd>
                                                                        </div>
                                                                    </dl>
                                                                    <pre className={styles.body}>
                                                                        {message.body_text || '(No plain-text body)'}
                                                                    </pre>
                                                                </div>
                                                            )}
                                                        </section>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    )}
                                    {detail && threadMessages.length === 0 && (
                                        <div className={styles.stateSmall}>
                                            This email had no readable plain-text body. Download the raw EML to view it.
                                        </div>
                                    )}
                                    <div className={styles.threadActions}>
                                        <button
                                            type="button"
                                            className={styles.headerButton}
                                            onClick={() => downloadRaw(communication)}
                                            disabled={downloadingId === communication.id}
                                        >
                                            <FaDownload />{' '}
                                            {downloadingId === communication.id ? 'Downloading…' : 'Download raw EML'}
                                        </button>
                                        {canManage && (
                                            <button
                                                type="button"
                                                className={styles.dangerButton}
                                                onClick={() => removeCommunication(communication)}
                                                disabled={removingId === communication.id}
                                            >
                                                <FaTrashAlt />{' '}
                                                {removingId === communication.id ? 'Removing…' : 'Remove email'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </article>
                    )
                })}
            </div>
        </CollapsibleSection>
    )
}
